import { Component, Optional } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import type { ErrorCode } from '@libs/errors/error-codes';
import { AuthService } from '@services/auth/auth.service';
import { MfaService } from '@services/auth/mfa.service';
import { AuthFormComponent } from '@shared/components/auth-form/auth-form.component';
import { translateApiError } from 'app/core/utils/api-error';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { environment } from '../../../../environments/environment';

type DevLoginUser = { id: string; email: string; name: string; role: string | null };

@Component({
	selector: 'app-login',
	imports: [AuthFormComponent, TranslocoPipe],
	templateUrl: './login.component.html',
	styleUrl: './login.component.scss',
})
export class LoginComponent {
	isRegister: 1 | 2 | 3 | 4 = 1; // 1 = Login, 4 = MFA
	errorMessage: string | null = null;
	isLoading = false;

	// MFA state
	mfaAccessToken: string | null = null;
	mfaFactorId: string | null = null;

	devLoginVisible = this.isLocalDev();
	devLoginOpen = false;
	devLoginLoading = false;
	devLoginUsers: DevLoginUser[] = [];

	constructor(
		private authService: AuthService,
		private mfaService: MfaService,
		private translocoService: TranslocoService,
		@Optional() public ref: DynamicDialogRef,
	) {}

	onLogin(formData: { email?: string; password?: string; mfaCode?: string; trustDevice?: boolean }): void {
		this.errorMessage = null;

		// Handle MFA verification
		if (this.isRegister === 4 && formData.mfaCode) {
			this.onVerifyMFA(formData.mfaCode, formData.trustDevice || false);
			return;
		}

		// Handle regular login
		if (!formData.email || !formData.password) {
			this.errorMessage = this.translocoService.translate('auth.errors.emailPasswordRequired');
			return;
		}

		const { email, password } = formData;

		this.isLoading = true;
		this.authService.login(email, password).subscribe({
			next: (response) => {
				this.isLoading = false;
				// Check if MFA is required
				if (response.mfa_required) {
					this.isRegister = 4; // Switch to MFA mode
					this.mfaAccessToken = response.access_token;
					this.mfaFactorId = response.factor_id;
				}
			},
			error: (err) => {
				this.isLoading = false;
				this.errorMessage = err.message || 'Ocurrió un error desconocido.';
			},
		});
	}

	onVerifyMFA(mfaCode: string, trustDevice: boolean): void {
		if (!mfaCode || !this.mfaAccessToken || !this.mfaFactorId) {
			this.errorMessage = this.translocoService.translate('auth.login.mfa.invalidCode');
			return;
		}

		this.isLoading = true;
		this.mfaService.verifyLogin(this.mfaAccessToken, this.mfaFactorId, mfaCode, trustDevice).subscribe({
			next: () => {
				this.isLoading = false;
				// Reset MFA state
				this.isRegister = 1;
				this.mfaAccessToken = null;
				this.mfaFactorId = null;
				this.errorMessage = null;
			},
			error: (err: { code?: ErrorCode; message?: string }) => {
				this.isLoading = false;
				// Too many attempts is not a wrong code: say so (translated by the interceptor)
				this.errorMessage =
					err?.code === 'TOO_MANY_REQUESTS' && err.message
						? err.message
						: this.translocoService.translate('auth.login.mfa.invalidCode');
			},
		});
	}

	cancelMFA(): void {
		this.isRegister = 1;
		this.mfaAccessToken = null;
		this.mfaFactorId = null;
		this.errorMessage = null;
	}

	toggleDevLogin(): void {
		this.devLoginOpen = !this.devLoginOpen;
		if (this.devLoginOpen && this.devLoginUsers.length === 0) {
			this.loadDevLoginUsers();
		}
	}

	forceDevLogin(userId: string): void {
		if (!userId || this.devLoginLoading) return;

		this.errorMessage = null;
		this.devLoginOpen = false;
		this.devLoginLoading = true;
		this.isLoading = true;

		this.authService.devLogin(userId).subscribe({
			error: (err) => {
				this.errorMessage = translateApiError(err, this.translocoService, 'auth.login.dev.loginError');
				this.devLoginLoading = false;
				this.isLoading = false;
				this.devLoginOpen = true;
			},
		});
	}

	private loadDevLoginUsers(): void {
		this.devLoginLoading = true;
		this.authService.getDevLoginUsers().subscribe({
			next: (users) => {
				this.devLoginUsers = users;
			},
			error: (err) => {
				this.errorMessage = translateApiError(err, this.translocoService, 'auth.login.dev.usersError');
				this.devLoginLoading = false;
			},
			complete: () => {
				this.devLoginLoading = false;
			},
		});
	}

	private isLocalDev(): boolean {
		if (environment.production || typeof window === 'undefined') return false;

		return this.isLocalHost(window.location.hostname) || this.isLocalBackend();
	}

	private isLocalBackend(): boolean {
		try {
			return this.isLocalHost(new URL(environment.pathBackend).hostname);
		} catch {
			return false;
		}
	}

	private isLocalHost(host: string): boolean {
		const normalized = host.toLowerCase();
		return (
			['localhost', '127.0.0.1', '::1', '[::1]', '0.0.0.0'].includes(normalized) ||
			normalized.endsWith('.localhost') ||
			/^10\.\d{1,3}\.\d{1,3}\.\d{1,3}$/.test(normalized) ||
			/^192\.168\.\d{1,3}\.\d{1,3}$/.test(normalized) ||
			/^172\.(1[6-9]|2\d|3[0-1])\.\d{1,3}\.\d{1,3}$/.test(normalized) ||
			// Tailscale CGNAT range (100.64.0.0/10): the VPS dev servers are reached via the tailnet IP.
			/^100\.(6[4-9]|[7-9]\d|1[0-1]\d|12[0-7])\.\d{1,3}\.\d{1,3}$/.test(normalized)
		);
	}
}
